import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Draggable } from 'react-beautiful-dnd';
import { Field } from 'src/API';
import { TEST_IDS } from 'src/test-ids';
import {
  FIELD_TYPES,
  FieldInput,
  getFieldTypeDisplayName,
  getFieldTypeDisplayNameFromValue,
} from 'src/api/process-module';
import ConfirmPopover from 'src/components/confirm-popover';
import { FieldNameLabelInputs } from './FieldNameLabelInputs';
import { FieldTypeConfig } from './FieldTypeConfig';

const disabledFields = [
  FIELD_TYPES.ACTIVITY_TABLE,
  FIELD_TYPES.DYNAMIC_TABLE,
  FIELD_TYPES.STATIC_FILE,
  FIELD_TYPES.PROCESS_MODULE_INFINITE_CANVAS, // Deprecated - only show if already exists
];

interface FieldEditorProps {
  field: FieldInput & Partial<Field>;
  index: number;
  handleFieldChange: (index: number, field: Partial<FieldInput>) => void;
  handleRemoveField: (index: number) => void;
  handleFieldSelection: (index: number, fieldId: string) => void;
  handleSaveField: (index: number) => Promise<void>;
  isSubmitting: boolean;
  isFieldUsingExisting: (index: number) => boolean;
  isFieldPendingSave: (index: number) => boolean;
  isRootCanvasFieldConfigured: (index: number) => boolean;
  moduleId: string;
  allFormFields?: (FieldInput & Partial<Field>)[];
  expanded?: boolean;
  onAccordionChange?: (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

export const FieldEditor = ({
  field,
  index,
  handleFieldChange,
  handleRemoveField,
  handleFieldSelection,
  handleSaveField,
  isSubmitting,
  isFieldUsingExisting,
  isFieldPendingSave,
  isRootCanvasFieldConfigured,
  moduleId,
  allFormFields,
  expanded = false,
  onAccordionChange,
}: FieldEditorProps) => {
  const isRootCanvasConfigured = isRootCanvasFieldConfigured(index);
  const isRootCanvas = field.type === FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS;
  return (
    <Draggable key={field.id} draggableId={field.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            marginBottom: '8px',
          }}
        >
          <Accordion expanded={expanded} onChange={onAccordionChange ? onAccordionChange(index) : undefined}>
            <AccordionSummary>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div {...provided.dragHandleProps} style={{ marginRight: '8px' }}>
                  <DragIndicatorIcon />
                </div>
                <Typography variant="subtitle1">
                  {index + 1}. {getFieldTypeDisplayNameFromValue(field.type || '')}
                  {field.id.startsWith('temp-') && (
                    <Typography component="span" color="error" sx={{ ml: 1 }}>
                      (Unsaved)
                    </Typography>
                  )}
                  {field.type === FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS &&
                    (!field.properties?.canvasId || !field.properties?.canvasCreated) && (
                      <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                        (Needs Configuration)
                      </Typography>
                    )}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel>Field Type</InputLabel>
                  <Select
                    value={field.type}
                    label="Field Type"
                    onChange={(e) =>
                      handleFieldChange(index, {
                        type: e.target.value as keyof typeof FIELD_TYPES,
                      })
                    }
                    disabled={isSubmitting || isFieldUsingExisting(index)}
                    data-testid={TEST_IDS.FIELD_EDITOR.TYPE_SELECT}
                  >
                    {Object.entries(FIELD_TYPES)
                      .sort()
                      .filter(([key]) => {
                        return !disabledFields.includes(FIELD_TYPES[key] as any);
                      })
                      .map(([key, value]) => (
                        <MenuItem key={value} value={value}>
                          {getFieldTypeDisplayName(key)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FieldTypeConfig
                  field={field}
                  index={index}
                  moduleId={moduleId}
                  handleFieldChange={handleFieldChange}
                  isSubmitting={isSubmitting}
                  onFieldSelect={() => handleFieldSelection(index, field.id)}
                  allFormFields={allFormFields}
                />
                <FieldNameLabelInputs
                  field={field}
                  index={index}
                  handleFieldChange={handleFieldChange}
                  isSubmitting={isSubmitting}
                  isFieldUsingExisting={isFieldUsingExisting}
                />
              </Stack>
              <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveField(index)}
                  disabled={isSubmitting || isFieldPendingSave(index) || (isRootCanvas && !isRootCanvasConfigured)}
                  color={isFieldPendingSave(index) ? 'warning' : 'primary'}
                  title={
                    isRootCanvas && !isRootCanvasConfigured
                      ? 'Please configure the canvas first (select template and click "Configure Canvas")'
                      : ''
                  }
                  data-testid={TEST_IDS.FIELD_EDITOR.CREATE_BUTTON}
                >
                  {field.id.startsWith('temp-')
                    ? isFieldPendingSave(index)
                      ? 'Creating...'
                      : 'Create Field'
                    : isFieldPendingSave(index)
                      ? 'Saving...'
                      : 'Save Field'}
                </Button>
                {isRootCanvas && !isRootCanvasConfigured && (
                  <Typography variant="caption" color="error" sx={{ alignSelf: 'center', ml: 1 }}>
                    Configure canvas first
                  </Typography>
                )}
                <ConfirmPopover
                  title=""
                  content="Are you sure you want to delete this field?"
                  onConfirm={() => handleRemoveField(index)}
                  onCancel={() => {}}
                  component={(handleClick) => (
                    <IconButton onClick={handleClick} disabled={isSubmitting} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  )}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </div>
      )}
    </Draggable>
  );
};
